/**
 * Health Monitor - Data-Driven Connection Resilience (Passive Observer)
 *
 * ISSUE_045: Data Flow Freshness 기반 상태 머신
 * ISSUE_047: Refactored to passive observer role, recovery delegated to AuthBroker
 *
 * State Machine:
 * - CONNECTED: delta < 3분
 * - DEGRADED: 3분 <= delta < 10분
 * - DISCONNECTED: delta >= 10분
 *
 * 핵심 원칙:
 * - setInterval DB 쿼리 하트비트 금지
 * - navigator.onLine은 힌트로만 사용 (즉시 DISCONNECTED 금지)
 * - 능동적 복구는 AuthBroker에 위임
 * - UI 상태 변경은 디바운스 적용
 *
 * @module core/resilience/HealthMonitor
 */

import { isSupabaseConfigured, pingSupabase, supabase } from '~/shared/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  useHealthStore,
  DEGRADED_THRESHOLD_MS,
  DISCONNECTED_THRESHOLD_MS,
  WS_STALE_THRESHOLD_MS,
  type HealthStatus,
  type UIConnectionStatus,
} from './healthStore';

// =============================================================================
// CONSTANTS
// =============================================================================

/** 상태 머신 체크 간격 (30초) */
const STATE_CHECK_INTERVAL = 30 * 1000;

/** ISSUE_073: WS Heartbeat 상수 */
const HEARTBEAT_CHANNEL_NAME = '_noten_hb';
const HEARTBEAT_EVENT = 'hb';
const HEARTBEAT_INTERVAL_MS = 30 * 1000;
const HEARTBEAT_TIMEOUT_MS = 10 * 1000;
/** 연속 echo 실패 N회 시 채널 재구독 */
const HEARTBEAT_MAX_CONSECUTIVE_FAILURES = 3;

// =============================================================================
// HEALTH MONITOR
// =============================================================================

export type HealthEventType =
  | 'status_changed'
  | 'degraded_entered'
  | 'disconnected_entered'
  | 'connected_restored'
  | 'ws_stale';

export type HealthEventHandler = (event: {
  type: HealthEventType;
  from?: HealthStatus;
  to?: HealthStatus;
  details?: string;
}) => void;

class HealthMonitor {
  private stateCheckInterval: ReturnType<typeof setInterval> | null = null;
  private eventHandlers: HealthEventHandler[] = [];
  private boundHandleVisibility: () => void;
  private boundHandleOnline: () => void;
  private boundHandleOffline: () => void;

  // ISSUE_073: ws_stale edge trigger (F1)
  private wasWsStale = false;

  // ISSUE_073: WS Heartbeat
  private heartbeatChannel: RealtimeChannel | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pendingHeartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
  private heartbeatRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatRetryAttempted = false;
  private consecutiveHeartbeatFailures = 0;

  constructor() {
    this.boundHandleVisibility = this.handleVisibilityChange.bind(this);
    this.boundHandleOnline = this.handleOnline.bind(this);
    this.boundHandleOffline = this.handleOffline.bind(this);
  }

  /**
   * 헬스 모니터 시작
   * ISSUE_047: Simplified to passive observer role
   */
  start(): void {
    if (!isSupabaseConfigured()) {
      return;
    }

    // 상태 머신 루프 시작
    this.startStateLoop();

    // ISSUE_073: WS heartbeat 시작
    this.startHeartbeat();

    // 이벤트 리스너 설정
    this.setupEventListeners();

    // 초기 상태 체크 (3초 후)
    setTimeout(() => this.evaluateState(), 3000);
  }

  /**
   * 헬스 모니터 중지
   */
  stop(): void {
    if (this.stateCheckInterval) {
      clearInterval(this.stateCheckInterval);
      this.stateCheckInterval = null;
    }

    this.stopHeartbeat();
    this.removeEventListeners();
    this.eventHandlers = [];
    this.wasWsStale = false;
    this.heartbeatRetryAttempted = false;
    this.consecutiveHeartbeatFailures = 0;
  }

  /**
   * 이벤트 핸들러 등록
   */
  onEvent(handler: HealthEventHandler): () => void {
    this.eventHandlers.push(handler);
    return () => {
      this.eventHandlers = this.eventHandlers.filter(h => h !== handler);
    };
  }

  /**
   * 현재 상태
   */
  getStatus(): HealthStatus {
    return useHealthStore.getState().status;
  }

  /**
   * Realtime 이벤트 터치 (외부에서 호출)
   */
  touchRealtime(): void {
    useHealthStore.getState().touchRealtime();
    // 이벤트가 오면 즉시 상태 재평가
    this.evaluateState();
  }

  /**
   * Query 성공 터치 (외부에서 호출)
   */
  touchQueryOk(): void {
    useHealthStore.getState().touchQueryOk();
  }

  /**
   * Sync 성공 터치 (외부에서 호출)
   */
  touchSyncOk(): void {
    useHealthStore.getState().touchSyncOk();
    // Sync 성공 시 상태 재평가
    this.evaluateState();
  }

  // ===========================================================================
  // PRIVATE: STATE MACHINE
  // ===========================================================================

  /**
   * 상태 머신 루프 시작
   */
  private startStateLoop(): void {
    this.stateCheckInterval = setInterval(() => {
      this.evaluateState();
    }, STATE_CHECK_INTERVAL);
  }

  /**
   * 상태 평가 및 전이
   * ISSUE_047: Simplified - no longer triggers recovery, just tracks state
   * ISSUE_073: degraded 상태에서 능동 probe + WS stale 감지 + uiStatus 갱신
   */
  private evaluateState(): void {
    const store = useHealthStore.getState();
    const delta = store.getDelta();
    const currentStatus = store.status;

    // 상태 결정
    let newStatus: HealthStatus;

    if (delta < DEGRADED_THRESHOLD_MS) {
      newStatus = 'connected';
    } else if (delta < DISCONNECTED_THRESHOLD_MS) {
      newStatus = 'degraded';
    } else {
      newStatus = 'disconnected';
    }

    // 상태 전이 (내부 상태만 - UI는 uiStatus 필드에서 제어)
    if (newStatus !== currentStatus) {
      this.transitionTo(newStatus, currentStatus);
    }

    // ISSUE_073: degraded 상태에서 능동 probe
    if (newStatus === 'degraded' && store.shouldProbe()) {
      void this.executeProbe();
    }

    // ISSUE_073: UI 상태 계산 + store 갱신
    const wsStale = (Date.now() - store.lastWsHeartbeatAt) > WS_STALE_THRESHOLD_MS;
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const newUiStatus: UIConnectionStatus = offline ? 'offline' : wsStale ? 'stale_realtime' : 'ok';
    store.setUIStatus(newUiStatus);

    // ISSUE_073: connected 상태인데 WS만 stale → ws_stale 이벤트 (edge trigger: 진입 시 1회만)
    if (wsStale && !offline && newStatus === 'connected' && !this.wasWsStale) {
      this.emit({ type: 'ws_stale', details: `lastHB=${Math.round((Date.now() - store.lastWsHeartbeatAt) / 1000)}s ago` });
    }
    this.wasWsStale = wsStale && !offline && newStatus === 'connected';
  }

  /**
   * ISSUE_073: 능동 probe 실행
   * degraded 상태에서 서버 도달 가능성 확인
   */
  private async executeProbe(): Promise<void> {
    const store = useHealthStore.getState();

    try {
      const ok = await pingSupabase();
      if (ok) {
        store.markProbeSuccess();
      } else {
        store.markProbeFailure();
      }
    } catch {
      store.markProbeFailure();
    }
  }

  /**
   * 상태 전이
   * ISSUE_047: Simplified - only emits events, recovery handled by AuthBroker
   */
  private transitionTo(newStatus: HealthStatus, fromStatus: HealthStatus): void {
    const store = useHealthStore.getState();
    store.setStatus(newStatus);

    this.emit({
      type: 'status_changed',
      from: fromStatus,
      to: newStatus,
    });

    // Emit specific events for state transitions
    if (newStatus === 'degraded' && fromStatus === 'connected') {
      this.emit({ type: 'degraded_entered' });
    }

    if (newStatus === 'disconnected') {
      this.emit({ type: 'disconnected_entered' });
    }

    if (newStatus === 'connected' && fromStatus !== 'connected') {
      this.emit({ type: 'connected_restored' });
    }
  }

  // ===========================================================================
  // PRIVATE: EVENT LISTENERS
  // ===========================================================================

  /**
   * 이벤트 리스너 설정
   */
  private setupEventListeners(): void {
    document.addEventListener('visibilitychange', this.boundHandleVisibility);
    window.addEventListener('online', this.boundHandleOnline);
    window.addEventListener('offline', this.boundHandleOffline);
  }

  /**
   * 이벤트 리스너 제거
   */
  private removeEventListeners(): void {
    document.removeEventListener('visibilitychange', this.boundHandleVisibility);
    window.removeEventListener('online', this.boundHandleOnline);
    window.removeEventListener('offline', this.boundHandleOffline);
  }

  /**
   * Visibility change 핸들러
   * ISSUE_063: 탭 복귀 시 frozen 기간의 delta를 "끊김"으로 오판하지 않음.
   * 타임스탬프를 리셋하여 CONNECTED로 시작하고, 이후 실제 데이터 흐름으로 판단.
   */
  private handleVisibilityChange(): void {
    if (document.visibilityState !== 'visible') return;

    const store = useHealthStore.getState();
    const delta = store.getDelta();

    // 탭이 frozen 상태였으면 delta가 크지만, 네트워크가 끊긴 게 아님.
    // freshness 타임스탬프를 리셋하여 불필요한 DISCONNECTED 전이를 방지.
    if (delta >= DEGRADED_THRESHOLD_MS) {
      store.touchQueryOk();
    }

    // ISSUE_073: 탭 복귀 시 false stale 방지 — heartbeat 타이머가 스로틀되어 stale로 보임
    store.touchWsHeartbeat();

    this.evaluateState();
  }

  /**
   * Offline 이벤트 핸들러 — 힌트로만 사용
   */
  private handleOffline(): void {
    this.evaluateState();
  }

  /**
   * Online 이벤트 핸들러
   */
  private handleOnline(): void {
    // 약간의 지연 후 상태 확인 (네트워크 안정화)
    setTimeout(() => {
      this.evaluateState();
    }, 1000);
  }

  // ===========================================================================
  // PRIVATE: WS HEARTBEAT (ISSUE_073)
  // ===========================================================================

  /**
   * broadcast self-echo 채널 생성 + 인터벌 시작
   */
  private startHeartbeat(): void {
    if (!supabase) return;

    this.heartbeatChannel = supabase.channel(HEARTBEAT_CHANNEL_NAME, {
      config: { broadcast: { self: true } },
    });

    this.heartbeatChannel
      .on('broadcast', { event: HEARTBEAT_EVENT }, () => {
        // 에코 수신 → WS 라운드트립 증명
        if (this.pendingHeartbeatTimeout) {
          clearTimeout(this.pendingHeartbeatTimeout);
          this.pendingHeartbeatTimeout = null;
        }
        this.consecutiveHeartbeatFailures = 0;
        useHealthStore.getState().touchWsHeartbeat();
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          this.heartbeatRetryAttempted = false; // 성공 → 다음 에러 시 1회 재시도 허용
          this.sendHeartbeat();
          // 중복 방지
          if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
          this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), HEARTBEAT_INTERVAL_MS);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // 좀비 인터벌 정리
          if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
          }
          if (this.pendingHeartbeatTimeout) {
            clearTimeout(this.pendingHeartbeatTimeout);
            this.pendingHeartbeatTimeout = null;
          }
          // R1: 1회 자동 재시작 (연속 실패 시 무한 루프 방지)
          if (!this.heartbeatRetryTimer && !this.heartbeatRetryAttempted) {
            this.heartbeatRetryAttempted = true;
            this.heartbeatRetryTimer = setTimeout(() => {
              this.heartbeatRetryTimer = null;
              this.restartHeartbeat();
            }, 10_000);
          }
        }
      });
  }

  /**
   * broadcast 전송 + 타임아웃 설정
   */
  private sendHeartbeat(): void {
    if (!this.heartbeatChannel) return;
    // 탭 hidden 시 WS가 끊겨있으므로 전송 스킵 (REST fallback 스팸 방지)
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    // WS 채널이 joined 상태가 아니면 heartbeat 의미 없음 — echo는 WS로만 도착.
    // send()가 REST로 자동 폴백되며 SDK deprecated 경고를 찍는 경로도 여기.
    if (this.heartbeatChannel.state !== 'joined') return;

    this.heartbeatChannel.send({
      type: 'broadcast',
      event: HEARTBEAT_EVENT,
      payload: { ts: Date.now() },
    });

    // 에코 대기 타임아웃
    if (this.pendingHeartbeatTimeout) {
      clearTimeout(this.pendingHeartbeatTimeout);
    }
    this.pendingHeartbeatTimeout = setTimeout(() => {
      this.pendingHeartbeatTimeout = null;
      this.consecutiveHeartbeatFailures++;
      // 연속 실패 시 interval 멈추고 채널 재구독 (새 토큰으로)
      if (this.consecutiveHeartbeatFailures >= HEARTBEAT_MAX_CONSECUTIVE_FAILURES) {
        this.consecutiveHeartbeatFailures = 0;
        this.restartHeartbeat();
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }

  /**
   * 채널/타이머 정리
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.pendingHeartbeatTimeout) {
      clearTimeout(this.pendingHeartbeatTimeout);
      this.pendingHeartbeatTimeout = null;
    }
    if (this.heartbeatRetryTimer) {
      clearTimeout(this.heartbeatRetryTimer);
      this.heartbeatRetryTimer = null;
    }
    if (this.heartbeatChannel) {
      supabase?.removeChannel(this.heartbeatChannel);
      this.heartbeatChannel = null;
    }
  }

  /**
   * ISSUE_073: public — Coordinator가 채널 리셋 후 호출
   */
  restartHeartbeat(): void {
    this.stopHeartbeat();
    // 재시작 시 stale 타이머 리셋 — 새 채널 subscribe 전에 ws_stale 오판 방지
    useHealthStore.getState().touchWsHeartbeat();
    this.startHeartbeat();
  }

  // ===========================================================================
  // PRIVATE: EVENT EMIT
  // ===========================================================================

  /**
   * 이벤트 발행
   */
  private emit(event: {
    type: HealthEventType;
    from?: HealthStatus;
    to?: HealthStatus;
    details?: string;
  }): void {
    this.eventHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (err) {
        console.error('[Health] Event handler error:', err);
      }
    });
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const healthMonitor = new HealthMonitor();

/**
 * 헬스 모니터 시작
 */
export function startHealthMonitor(): void {
  healthMonitor.start();
}

/**
 * 헬스 모니터 중지
 */
export function stopHealthMonitor(): void {
  healthMonitor.stop();
}

/**
 * Realtime 이벤트 터치
 */
export function touchRealtimeEvent(): void {
  healthMonitor.touchRealtime();
}

/**
 * Query 성공 터치
 */
export function touchQuerySuccess(): void {
  healthMonitor.touchQueryOk();
}

/**
 * Sync 성공 터치
 */
export function touchSyncSuccess(): void {
  healthMonitor.touchSyncOk();
}
